import { Directive, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, EMPTY, forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, switchMap, take, withLatestFrom } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ApiThesaurusConcept,
    apiThesaurusConceptToReference,
    BaseContainer,
    cacheValueWhileSubscribed,
    DottedKey,
    FieldDefinition,
    isNotNullOrUndefined,
    ITreeViewerNode,
    PaginatedResults,
    ShFieldDefinitionService,
    ThesaurusConceptReference,
    throwIfNullOrUndefined,
    TreeViewerNode
} from '@stylehaven/components';
import { flatMapDeep } from 'lodash-es';
import { ThesaurusTreeSelectionMode } from '../../../common/entities/common/thesaurus-tree-selection.entity';
import { ThesaurusService } from '../../../common/services/thesaurus/thesaurus.service';
import { ExtractThesaurusId } from '../../../thesaurus/helpers/extract-thesaurus-id.helper';

@Directive({})
export abstract class ThesaurusTreeViewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    // Tree streams
    public thesaurusTopConceptNode$: Observable<TreeViewerNode<ApiThesaurusConcept>>;
    public searchTopConcept$: Observable<ThesaurusConceptReference | null>;
    public treeViewNodes$: Observable<TreeViewerNode<ApiThesaurusConcept>[]>;
    public thesaurusDottedKey$: ReplaySubject<DottedKey> = new ReplaySubject<DottedKey>(1);
    public thesaurusFieldDefinition$: Observable<FieldDefinition>;
    public suggestionErrorMsg$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    protected nodesUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    public suggestions$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public suggestionLimit$: Observable<boolean>;

    public hideRootNodes: boolean;
    public showSearch: boolean;

    protected currentNodes: TreeViewerNode<ApiThesaurusConcept>[];

    public thesaurusSearchControl: UntypedFormControl = new UntypedFormControl();

    @Input() set thesaurusDefDottedKey(key: DottedKey) {
        this.thesaurusDottedKey$.next(key);
    }

    @Input() id: string;
    @Input() disabled: boolean;
    @Input() selectionMode: ThesaurusTreeSelectionMode = ThesaurusTreeSelectionMode.ParentSelection;
    @Input() selectMultiple = false;
    @Input() enableSuggestions = false;

    @Input() set initialSuggestions(initialSuggestions: string[]) {
        this.suggestions$.next(initialSuggestions);
    }

    @Output() suggestions: EventEmitter<string[]> = new EventEmitter<string[]>();

    constructor(
        protected thesaurusService: ThesaurusService,
        protected fieldDefinitionService: ShFieldDefinitionService,
        protected translateService: SharedLanguageService
    ) {
        super();
    }

    protected abstract initSuggestionLimit(): void;
    protected abstract initEmissionStream(): void;
    protected abstract isActive(thesaurusConcept: ApiThesaurusConcept): boolean;
    protected abstract toggleConceptNodeState(concept: ApiThesaurusConcept | null, state: boolean): boolean;

    public abstract onSelectedNodesChange(nodes: ITreeViewerNode<ApiThesaurusConcept>[]): void;
    public abstract onLookupSelection(concept: ThesaurusConceptReference | null): void;

    ngOnInit(): void {
        this.initThesaurusNodeStream();
        this.initTreeViewSettings();
        this.initTreeViewNodes();
        this.initSearchTopConcept();
        this.initSuggestionLimit();
        this.initEmissionStream();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    private initTreeViewSettings() {
        this.hideRootNodes = this.selectionMode === ThesaurusTreeSelectionMode.ConceptSelection;
        this.showSearch = this.selectionMode !== ThesaurusTreeSelectionMode.ParentSelection;
    }

    private initTreeViewNodes() {
        this.treeViewNodes$ = combineLatest([this.nodesUpdated$, this.thesaurusTopConceptNode$]).pipe(
            map(([_, topConceptNode]) => this.currentNodes ?? [topConceptNode])
        );
    }

    private initSearchTopConcept() {
        this.searchTopConcept$ =
            this.selectionMode === ThesaurusTreeSelectionMode.ParentSelection
                ? this.thesaurusTopConceptNode$.pipe(
                      map(topConceptNode => topConceptNode.data),
                      map(topConcept => (topConcept ? apiThesaurusConceptToReference(topConcept) : null)),
                      isNotNullOrUndefined()
                  )
                : of(null);
    }

    initThesaurusNodeStream(): void {
        this.thesaurusFieldDefinition$ = this.thesaurusDottedKey$.pipe(
            switchMap(thesaurusDefDottedKey =>
                this.fieldDefinitionService.getByDottedKey(thesaurusDefDottedKey).pipe(
                    throwIfNullOrUndefined(),
                    catchError(error => {
                        console.error(`FieldDefinition of ${thesaurusDefDottedKey} could not be found.`);
                        return EMPTY;
                    }),
                    cacheValueWhileSubscribed()
                )
            )
        );

        const topConceptUri$: Observable<string> = this.thesaurusDottedKey$.pipe(
            switchMap(thesaurusDefFlatKey =>
                this.thesaurusFieldDefinition$.pipe(
                    map(fieldDefinition => fieldDefinition.Scheme?.TopConcept),
                    throwIfNullOrUndefined(),
                    catchError(error => {
                        console.error(`Scheme of FieldDefinition of ${thesaurusDefFlatKey} could not be found.`);
                        return EMPTY;
                    })
                )
            )
        );

        this.thesaurusTopConceptNode$ = this.thesaurusDottedKey$.pipe(
            switchMap(thesaurusDefDottedKey =>
                topConceptUri$.pipe(
                    switchMap(topConceptUri =>
                        this.thesaurusService.getThesaurusConceptByUri(thesaurusDefDottedKey, topConceptUri)
                    ),
                    withLatestFrom(this.thesaurusFieldDefinition$),
                    map(([rootConcept, fieldDefinition]) => this.conceptToNode(fieldDefinition, rootConcept)),
                    cacheValueWhileSubscribed()
                )
            )
        );
    }

    private getNarrowerConceptNodes(
        conceptUri: string,
        offset: number = 0
    ): Observable<PaginatedResults<TreeViewerNode<ApiThesaurusConcept>>> {
        const conceptId: string | undefined = ExtractThesaurusId(conceptUri);

        if (!conceptId) {
            const message = `ConceptId could not be extracted from ConceptUri ${conceptUri}`;
            console.error(message);
            throw new Error(message);
        }

        return this.thesaurusFieldDefinition$.pipe(
            switchMap(fieldDefinition =>
                this.thesaurusService.getThesaurusFieldNarrowerConcepts(fieldDefinition.DottedKey, conceptId, offset)
            ),
            withLatestFrom(this.thesaurusFieldDefinition$, this.thesaurusTopConceptNode$),
            map(([paginatedResults, fieldDefinition, thesaurusRootNode]) => ({
                ...paginatedResults,
                results: paginatedResults.results.map((concept: ApiThesaurusConcept) =>
                    this.conceptToNode(fieldDefinition, concept, thesaurusRootNode)
                )
            })),
            take(1)
        );
    }

    private conceptToNode(
        fieldDefinition: FieldDefinition,
        thesaurusConcept: ApiThesaurusConcept,
        thesaurusRootNode?: TreeViewerNode<ApiThesaurusConcept>
    ): TreeViewerNode<ApiThesaurusConcept> {
        const label: string = this.thesaurusService.getApiThesaurusConceptLabel(thesaurusConcept);

        const maxNarrower: number = fieldDefinition.Scheme?.MaxNarrower || 0;
        const minNarrower: number = fieldDefinition.Scheme?.MinNarrower || 0;

        const topConcept: ApiThesaurusConcept =
            thesaurusRootNode && thesaurusRootNode.data ? thesaurusRootNode.data : thesaurusConcept;

        const rootNodeLevel: number = topConcept.Broader.length + topConcept.BroaderTransitive.length;

        const conceptLevel: number = thesaurusConcept.Broader.length + thesaurusConcept.BroaderTransitive.length;
        const inLowerBounds: boolean = conceptLevel - rootNodeLevel >= minNarrower - 1;
        const inUpperBounds: boolean = conceptLevel - rootNodeLevel < maxNarrower;

        const inBounds: boolean = inLowerBounds && inUpperBounds;
        const disabled: boolean =
            this.selectionMode === ThesaurusTreeSelectionMode.ConceptSelection
                ? !thesaurusConcept.Selectable
                : !inBounds;

        const isActive: boolean = this.isActive(thesaurusConcept);

        return {
            id: thesaurusConcept.Uri,
            name: label,
            data: thesaurusConcept,
            hasChildren: inBounds && thesaurusConcept.Narrower.length > 0,
            disabled: disabled,
            isActive
        };
    }

    private onThesaurusSuggestionSubmit(newSuggestion: string, currentSuggestions: string[]) {
        if (currentSuggestions.includes(newSuggestion)) {
            this.suggestionErrorMsg$.next(this.translateService.translate('thesaurus.error.duplicate-suggestion'));
            return;
        }
        this.suggestionErrorMsg$.next(null);

        const updatedSuggestions: string[] = [...currentSuggestions, newSuggestion];
        this.suggestions$.next(updatedSuggestions);
        this.suggestions.next(updatedSuggestions);
    }

    protected findTreeNodeById(
        treeNodes: TreeViewerNode<ApiThesaurusConcept>[],
        conceptUri: string
    ): ITreeViewerNode<ApiThesaurusConcept> | undefined {
        const flattenedNodes: ITreeViewerNode<ApiThesaurusConcept>[] = flatMapDeep(treeNodes, this.getChildren);
        return flattenedNodes.find(node => node.id === conceptUri);
    }

    public getNodeNarrowerConcepts = (
        node: ITreeViewerNode<ApiThesaurusConcept>,
        offset: number = 0
    ): Observable<PaginatedResults<TreeViewerNode<ApiThesaurusConcept>>> => {
        return this.getNarrowerConceptNodes(node.id, offset);
    };

    public onSuggestion(suggestion: string) {
        this.onThesaurusSuggestionSubmit(suggestion, this.suggestions$.value);
    }

    public onUpdatedNodes(nodes: ITreeViewerNode<ApiThesaurusConcept>[]) {
        this.currentNodes = nodes;
    }

    // #region util

    // ENG-4806: Narrower concepts do not get full breadcrumbs so need to fetch complete concepts
    protected getFullBreadcrumbs(concepts: ApiThesaurusConcept[]): Observable<ApiThesaurusConcept[]> {
        return this.thesaurusFieldDefinition$.pipe(
            map(fieldDefinition =>
                concepts.map(concept =>
                    this.thesaurusService.getThesaurusConceptByUri(fieldDefinition.DottedKey, concept.Uri)
                )
            ),
            switchMap(fullConcepts => forkJoin(fullConcepts))
        );
    }

    protected getChildren: any = (child: TreeViewerNode<ApiThesaurusConcept>) => {
        if (!child.children || !child.children.length) {
            return child;
        }
        return [child, flatMapDeep(child.children, this.getChildren)];
    };

    // #endregion
}
